import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Puzzle, Users, BookOpen } from 'lucide-react';

interface Plugin {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  category: string;
  author: string;
  internalPath?: string;
  features: string[];
  usage: string[];
}

const plugins: Plugin[] = [
  {
    id: 'robopreview',
    name: 'Robo Preview',
    icon: <Puzzle size={24} className="text-blue-500" />,
    description: '基于 WebGL 的在线机器人 3D 模型预览工具，兼容 URDF/Xacro/MJCF 格式，支持拖拽即用，无需本地部署。',
    category: '平台插件',
    author: 'OakCreek Tech',
    internalPath: '/robo-preview',
    features: [
      '多格式兼容：支持 URDF、Xacro、MJCF 等机器人描述格式，即时渲染预览',
      '即拖即载：将模型文件或目录拖入浏览器即可快速加载',
      '关节可视化：直观呈现各关节的连接关系和运动约束',
      '碰撞体显示：可视化机器人的碰撞几何结构',
      '坐标系展示：呈现各连杆的坐标系朝向和位置信息',
      '关键帧编辑：支持实验性的动画关键帧与曲线调节功能',
      '动力学仿真：内置 MuJoCo 引擎实现实时物理仿真',
      '浏览器原生：基于 WebGL 渲染引擎，所有计算在本地完成，数据不外传',
    ],
    usage: [
      '在插件详情页点击「打开」按钮进入 Robo Preview',
      '将 URDF/Xacro/MJCF 文件拖拽到页面任意区域',
      '左键拖动旋转视角，滚轮缩放，右键平移画面',
      '通过左侧面板控制各关节的显示状态',
      '拖拽关节实现模型姿态的实时调整',
    ],
  },
];

export default function PluginSquare() {
  const navigate = useNavigate();
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);

  if (selectedPlugin) {
    return (
      <div className="h-full flex flex-col bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedPlugin(null)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <h1 className="text-lg font-bold text-slate-800">插件详情</h1>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            {/* Plugin Header */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100">
                    {selectedPlugin.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">{selectedPlugin.name}</h2>
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                      <Users size={14} /> {selectedPlugin.author}
                    </p>
                    <p className="text-sm text-slate-600 mt-2">{selectedPlugin.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => selectedPlugin.internalPath && navigate(selectedPlugin.internalPath)}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-sm"
                >
                  打开
                </button>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 mt-6 pt-4 border-t border-slate-100">
                <div className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                  {selectedPlugin.category}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen size={18} className="text-slate-600" />
                <h3 className="text-lg font-bold text-slate-800">详细介绍</h3>
              </div>

              <div className="space-y-6">
                {/* Features */}
                <div>
                  <h4 className="text-base font-bold text-slate-800 mb-3">功能特性</h4>
                  <ul className="space-y-2">
                    {selectedPlugin.features.map((feature, idx) => {
                      const [title, desc] = feature.split('：');
                      return (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />
                          <span><strong className="text-slate-700">{title}：</strong>{desc}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Usage */}
                <div>
                  <h4 className="text-base font-bold text-slate-800 mb-3">使用方法</h4>
                  <ol className="space-y-2">
                    {selectedPlugin.usage.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Platform Info */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <h4 className="text-sm font-bold text-slate-800 mb-2">平台信息</h4>
                  <p className="text-sm text-slate-600">
                    Robo Preview 是 OakCreek Tech 团队开发的网页端机器人3D模型预览工具。
                    兼容 URDF、Xacro、MJCF 等多种格式，拖拽即可加载，浏览器本地处理，确保数据安全。
                  </p>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">插件广场</h1>
            <p className="text-sm text-slate-500 mt-1">探索增强工作流的实用工具</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Plugin Grid */}
          <div className="flex justify-start">
            <div
              key={plugins[0].id}
              onClick={() => navigate(plugins[0].internalPath || '/robo-preview')}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group w-full max-w-md"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 group-hover:bg-blue-100 transition-colors shrink-0">
                  {plugins[0].icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                    {plugins[0].name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <Users size={14} /> {plugins[0].author}
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-4 line-clamp-3">{plugins[0].description}</p>
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
                <div className="ml-auto">
                  <span className="px-2 py-0.5 bg-slate-100 rounded-full text-[10px] font-medium text-slate-500">
                    {plugins[0].category}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Empty State for Future Plugins */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-400">更多插件正在开发中，敬请期待...</p>
          </div>
        </div>
      </div>
    </div>
  );
}
